/**
 * Catálogo de textos en español (europeo) para la funcionalidad de autenticación
 * (inicio de sesión, registro, verificación de enlace mágico, restablecimiento /
 * establecimiento de contraseña). Las claves se corresponden 1:1 con las llamadas
 * `t('auth.*')` en `packages/@gamecodex/world-ui/src/features/auth/*`. Los valores
 * son las cadenas de texto originales en español.
 */
export const authEs = {
  // ── AuthShell ────────────────────────────────────────────────────────────
  'auth.shell.homeAriaLabel': 'Inicio de Grymwyrd',
  'auth.shell.logoAlt': 'Grymwyrd',

  // ── OAuthButtons ─────────────────────────────────────────────────────────
  'auth.oauth.continueWithGoogle': 'Continuar con Google',
  'auth.oauth.continueWithDiscord': 'Continuar con Discord',

  // ── AccountVerifyPage ────────────────────────────────────────────────────
  'auth.verify.seoTitle': 'Iniciando sesión',
  'auth.verify.seoDescription': 'Verificando tu enlace de inicio de sesión en Grymwyrd.',
  'auth.verify.errorMissingToken':
    'Este enlace de inicio de sesión no tiene su token. Solicita uno nuevo desde la página de inicio de sesión.',
  'auth.verify.errorInvalidOrExpired':
    'Este enlace de inicio de sesión no es válido o ha caducado. Solicita uno nuevo desde la página de inicio de sesión.',
  'auth.verify.confirming': 'Confirmando tu inicio de sesión…',
  'auth.verify.signedInHeading': 'Has iniciado sesión',
  'auth.verify.signedInAsPrefix': 'Sesión iniciada como',
  'auth.verify.signedInAsSuffix': '. Te llevamos a tu santuario…',
  'auth.verify.takingYouToSanctum': 'Te llevamos a tu santuario…',
  'auth.verify.goNow': 'Ir ahora',
  'auth.verify.problemHeading': 'Problema con el enlace de inicio de sesión',
  'auth.verify.backToSignIn': 'Volver al inicio de sesión',

  // ── LoginPage ────────────────────────────────────────────────────────────
  'auth.login.errorEnterEmailFirst': 'Introduce tu correo electrónico arriba y solicita un enlace.',
  'auth.login.seoTitle': 'Cruza el Umbral',
  'auth.login.seoDescription': 'Inicia sesión en Grymwyrd y regresa a tu leyenda en el crepúsculo eterno.',
  'auth.login.eyebrow': 'Regresa, Peregrino',
  'auth.login.title': 'Cruza el Umbral',
  'auth.login.blurb': 'Pronuncia tu nombre y la palabra secreta para reentrar en el crepúsculo eterno.',
  'auth.login.noPactYet': '¿Aún sin pacto?',
  'auth.login.forgeYourLegend': 'Forja tu leyenda',
  'auth.login.emailLabel': 'Correo electrónico',
  'auth.login.emailPlaceholder': 'tu@grymwyrd.com',
  'auth.login.passwordLabel': 'Contraseña',
  'auth.login.forgotPassword': '¿Olvidaste tu contraseña?',
  'auth.login.submitBusy': 'Cruzando…',
  'auth.login.submit': 'Entrar',
  'auth.login.dividerOr': 'o',
  'auth.login.magicSentPrefix': 'Un enlace de inicio de sesión está en camino',
  'auth.login.magicSentTo': 'a',
  'auth.login.magicSentSuffix': '. Revisa tu bandeja de entrada.',
  'auth.login.emailMeSignInLink': 'Envíame un enlace de inicio de sesión',
  'auth.login.mockAuthPrefix': 'Auth simulada de desarrollo: cualquier correo funciona. Usa',
  'auth.login.mockAuthOr': 'o',
  'auth.login.mockAuthSuffix': 'para roles elevados.',

  // ── RegisterPage ─────────────────────────────────────────────────────────
  'auth.register.errorDisplayNameRequired': 'Elige el nombre bajo el que tu leyenda será contada.',
  'auth.register.errorEmailRequired': 'Se requiere un correo electrónico para sellar tu pacto.',
  'auth.register.errorEmailInvalid': 'Ese correo electrónico no parece correcto.',
  'auth.register.errorPasswordTooShort': 'Tu palabra secreta debe tener al menos 8 caracteres.',
  'auth.register.errorMustBeAdult': 'Debes tener 13 años o más para unirte a Grymwyrd.',
  'auth.register.errorMustAcceptTerms': 'Debes aceptar el pacto (términos) para continuar.',
  'auth.register.errorIncomplete': 'Algunas marcas en tu pacto están incompletas. Mira más abajo.',
  'auth.register.seoTitle': 'Comienza Tu Historia',
  'auth.register.seoDescription':
    'Crea una cuenta de Grymwyrd, levanta un avatar y haz que tu historia sea canónica.',
  'auth.register.eyebrow': 'Explora el Pueblo de Greywick',
  'auth.register.title': 'Comienza Tu Historia',
  'auth.register.blurb': 'Despiertas de sueños etereos y abres los ojos…',
  'auth.register.alreadyBound': '¿Ya ligado al crepúsculo?',
  'auth.register.loginToExisting': 'Inicia Sesión en Tu Cuenta Existente',
  'auth.register.displayNameLabel': 'Nombre visible',
  'auth.register.displayNamePlaceholder': 'El Caballero Hueco',
  'auth.register.displayNameHint': 'Esto nombra tu avatar público y tus contribuciones.',
  'auth.register.emailLabel': 'Correo electrónico',
  'auth.register.emailPlaceholder': 'tu@grymwyrd.com',
  'auth.register.passwordLabel': 'Contraseña',
  'auth.register.passwordPlaceholder': 'Al menos 8 caracteres',
  'auth.register.agreementsLegend': 'Acuerdos',
  'auth.register.adultConfirmPrefix': 'Confirmo que tengo',
  'auth.register.adultConfirmEmphasis': '13 años o más',
  'auth.register.adultConfirmSuffix': '.',
  'auth.register.acceptPrefix': 'Acepto los',
  'auth.register.termsOfThePact': 'Términos del Pacto',
  'auth.register.acceptSuffix': '.',
  'auth.register.submitBusy': 'Sellando el pacto…',
  'auth.register.submit': 'Emerger',
  'auth.register.dividerOr': 'o',
  'auth.register.mockAuthNote': 'Auth simulada de desarrollo: el registro persiste localmente solo. No se envía correo electrónico.',
  'auth.register.lastUpdated': 'Última actualización {date}',

  // ── ResetPasswordPage ────────────────────────────────────────────────────
  'auth.reset.errorInvalidEmail': 'Introduce el correo electrónico vinculado a tu pacto.',
  'auth.reset.seoTitle': 'Restablece Tu Contraseña',
  'auth.reset.seoDescription':
    '¿Olvidaste tu contraseña? Te enviaremos un enlace de un solo uso; haz clic en él para elegir una nueva contraseña.',
  'auth.reset.eyebrow': 'Perdiste la Palabra',
  'auth.reset.title': 'Restablece Tu Contraseña',
  'auth.reset.blurb':
    'Susurra tu correo electrónico y se enviará un enlace de un solo uso a la oscuridad. Síguelo de vuelta y establecerás una nueva contraseña.',
  'auth.reset.rememberedAfterAll': '¿Lo recordaste después de todo?',
  'auth.reset.loginToExisting': 'Inicia Sesión en Tu Cuenta Existente',
  'auth.reset.successMessage':
    'Si existe una cuenta para {email}, se ha enviado un enlace de un solo uso. Ábrelo para iniciar sesión y establecer tu nueva contraseña — revisa tu correo electrónico y la cripta de spam.',
  'auth.reset.sendToDifferentEmail': 'Enviar a un correo electrónico diferente',
  'auth.reset.emailLabel': 'Correo electrónico',
  'auth.reset.emailPlaceholder': 'tu@grymwyrd.com',
  'auth.reset.submitBusy': 'Invocando…',
  'auth.reset.submit': 'Enviar Enlace de Inicio de Sesión',

  // ── SetPasswordPage ──────────────────────────────────────────────────────
  'auth.setPassword.errorTooShort': 'Elige una contraseña de al menos 8 caracteres.',
  'auth.setPassword.errorMismatch': 'Esas contraseñas no coinciden.',
  'auth.setPassword.seoTitle': 'Establece Tu Contraseña',
  'auth.setPassword.seoDescription': 'Elige una nueva contraseña para tu cuenta de Grymwyrd.',
  'auth.setPassword.eyebrow': 'Sella el Pacto',
  'auth.setPassword.title': 'Establece Una Nueva Contraseña',
  'auth.setPassword.blurb':
    'Has iniciado sesión. Elige una nueva contraseña — podrás usarla la próxima vez junto con el enlace mágico.',
  'auth.setPassword.ratherNot': '¿Prefieres no hacerlo?',
  'auth.setPassword.skipForNow': 'Omitir por ahora',
  'auth.setPassword.successMessage': 'Tu contraseña está establecida. Te llevamos a tu santuario…',
  'auth.setPassword.newPasswordLabel': 'Nueva contraseña',
  'auth.setPassword.newPasswordPlaceholder': 'Al menos 8 caracteres',
  'auth.setPassword.confirmPasswordLabel': 'Confirmar contraseña',
  'auth.setPassword.confirmPasswordPlaceholder': 'Escríbela de nuevo',
  'auth.setPassword.submitBusy': 'Sellando…',
  'auth.setPassword.submit': 'Guardar Contraseña',
} as const
